import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CORE_MODEL_INFO, isModelAvailableOnRoute } from '../../utils/coreModel';
import type { CoreModel, OSRoute } from '../../utils/coreModel';
import { CORE_SETUP_KEYS } from '../../utils/localStorage';
import { logger } from '../../utils/logger';
import { getModelFromSku } from '../../utils/skuToModel';

const guideLogger = logger.withComponent('InstallationGuide');

/**
 * Device model types supported by the installation guide - see CoreModel in ../../utils/coreModel
 */
export type { CoreModel };

const CORE_MODELS: CoreModel[] = Object.keys(CORE_MODEL_INFO) as CoreModel[];

// A stored model only counts if it's a recognized model AND actually offered on this route
// (e.g. a stale/cross-route localStorage value for a model that isn't available here is ignored).
// Exported so any other localStorage-reading code (e.g. ConfirmModelStep) validates a stored
// model the same way, rather than re-implementing an equivalent check that can drift.
export function isCoreModel(value: string | null, route: OSRoute): value is CoreModel {
  return value !== null && CORE_MODELS.includes(value as CoreModel) && isModelAvailableOnRoute(value as CoreModel, route);
}

/**
 * Connection status during device reachability test
 */
export type ConnectionStatus = 'idle' | 'testing' | 'cloud' | 'local' | 'unreachable';

/**
 * Session state for guard rails
 */
export type SessionState = 'loading' | 'ok' | 'missing_or_expired';

/**
 * Step definition for the installation guide wizard
 */
export interface StepDefinition {
  id: string;
  stepNumber: number | null; // null for steps that don't show in step indicator
}

/**
 * State managed by the InstallationGuideProvider
 */
export interface InstallationGuideState {
  // Navigation
  currentStepIndex: number;
  stepHistory: number[];

  // Route context
  isV2Route: boolean;

  // Device Information (from localStorage)
  sessionId: string | null;
  deviceSerial: string | null;
  deviceModel: CoreModel | null;
  deviceSku: string | null;
  orgId: string | null;

  // Connection Test
  connectionStatus: ConnectionStatus;
  connectionRetryCount: number;
  deviceUrl: string | null;

  // UI State
  activeLayer: string | null;

  // Tracking
  mainStepId: string;

  // Session/guard rails
  sessionState: SessionState;
}

/**
 * Actions available from the InstallationGuideContext
 */
export interface InstallationGuideActions {
  // Navigation
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (stepId: string) => void;
  
  // State updates
  setDeviceModel: (model: CoreModel) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setDeviceUrl: (url: string | null) => void;
  incrementRetryCount: () => void;
  resetRetryCount: () => void;
  setActiveLayer: (layer: string | null) => void;
  setMainStepId: (stepId: string) => void;
  setSessionState: (state: SessionState) => void;
}

/**
 * Combined context value
 */
export interface InstallationGuideContextValue extends InstallationGuideState, InstallationGuideActions {
  // Computed values
  currentStepId: string;
  currentStepNumber: number | null;
  totalSteps: number;
  showBackButton: boolean;
}

/**
 * Step definitions for the installation guide
 * stepNumber: null means the step doesn't show in the step indicator
 */
export const STEPS: StepDefinition[] = [
  { id: 'loading', stepNumber: null },
  { id: 'welcome', stepNumber: null },
  { id: 'session-missing', stepNumber: null },
  { id: 'guide-error', stepNumber: null },
  { id: 'confirm-model', stepNumber: 1 },
  { id: 'readiness-check', stepNumber: 2 },
  { id: 'box-contents', stepNumber: 3 },
  { id: 'placement', stepNumber: 4 },
  { id: 'connect-overview', stepNumber: 5 },
  { id: 'connect-router', stepNumber: 5 }, // Same step number as overview
  { id: 'connect-core', stepNumber: 6 },
  { id: 'connect-power', stepNumber: 7 },
  { id: 'confirm-power', stepNumber: 8 },
  { id: 'ready-test', stepNumber: 9 },
  { id: 'connection-test', stepNumber: 9 }, // Same step number as ready-test
];

/** Total number of user-visible steps (unique step numbers) */
export const TOTAL_VISIBLE_STEPS = 9;

// Create context with undefined default
export const InstallationGuideContext = createContext<InstallationGuideContextValue | undefined>(undefined);

interface InstallationGuideProviderProps {
  children: React.ReactNode;
}

/**
 * Helper to read localStorage value with TTL validation
 */
function getLocalStorageValue(key: string): string | null {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const parsed = JSON.parse(item);
    
    // Check for { value, expiresAt } structure
    if (parsed && typeof parsed === 'object' && 'value' in parsed) {
      // Check expiration if present
      if ('expiresAt' in parsed && typeof parsed.expiresAt === 'number') {
        if (Date.now() > parsed.expiresAt) {
          guideLogger.debug(`localStorage key ${key} has expired`);
          return null;
        }
      }
      return parsed.value;
    }

    // For backward compatibility, return raw value if it's a string
    if (typeof parsed === 'string') {
      return parsed;
    }

    return null;
  } catch {
    // If JSON parse fails, try returning raw value (backward compatibility)
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
}

/**
 * InstallationGuideProvider - Context provider for installation guide state
 */
export const InstallationGuideProvider: React.FC<InstallationGuideProviderProps> = ({ children }) => {
  const location = useLocation();
  const isV2Route = location.pathname.startsWith('/v2');

  // Navigation state
  const [currentStepIndex, setCurrentStepIndex] = useState(0); // Start at loading
  const [stepHistory, setStepHistory] = useState<number[]>([]);

  // Device information
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [deviceSerial, setDeviceSerial] = useState<string | null>(null);
  const [deviceModel, setDeviceModelState] = useState<CoreModel | null>(null);
  const [deviceSku, setDeviceSku] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [orgId, _setOrgId] = useState<string | null>(null);

  // Connection test state
  const [connectionStatus, setConnectionStatusState] = useState<ConnectionStatus>('idle');
  const [connectionRetryCount, setConnectionRetryCount] = useState(0);
  const [deviceUrl, setDeviceUrlState] = useState<string | null>(null);

  // UI state
  const [activeLayer, setActiveLayerState] = useState<string | null>(null);
  const [mainStepId, setMainStepIdState] = useState('');
  const [sessionState, setSessionStateState] = useState<SessionState>('loading');

  // Read localStorage on mount
  useEffect(() => {
    const loadSessionData = () => {
      const route: OSRoute = isV2Route ? 'v2' : 'v1';
      const storedSessionId = getLocalStorageValue(CORE_SETUP_KEYS.SESSION_ID);
      const storedSerial = getLocalStorageValue(CORE_SETUP_KEYS.DEVICE_SERIAL_NUMBER);
      const storedModelRaw = getLocalStorageValue(CORE_SETUP_KEYS.DEVICE_MODEL);
      const storedModel = isCoreModel(storedModelRaw, route) ? storedModelRaw : null;
      const storedDerivedModelRaw = getLocalStorageValue(CORE_SETUP_KEYS.DERIVED_MODEL);
      const storedDerivedModel = isCoreModel(storedDerivedModelRaw, route) ? storedDerivedModelRaw : null;
      const storedSku = getLocalStorageValue(CORE_SETUP_KEYS.DEVICE_SKU);

      // V2: Use stored model only (no SKU fallback)
      // V1: Use stored model or derive from SKU
      let effectiveModel = storedModel;
      let modelSource = 'localStorage';

      if (!storedModel) {
        if (isV2Route) {
          guideLogger.info('V2 route: No model in localStorage, will show model selection');
          modelSource = 'none';
        } else {
          if (storedSku) {
            const derivedModel = getModelFromSku(storedSku);
            if (derivedModel) {
              effectiveModel = derivedModel;
              modelSource = 'derived from SKU';
              guideLogger.info(`V1 route: Derived device model from SKU: ${storedSku} -> ${derivedModel}`);
            } else {
              guideLogger.debug(`V1 route: Unknown SKU: ${storedSku}, model will need manual selection`);
              modelSource = 'unknown';
            }
          } else {
            modelSource = 'unknown';
          }
        }
      }

      guideLogger.info('Loaded session data from localStorage', {
        route: isV2Route ? '/v2' : '/v1',
        hasSessionId: !!storedSessionId,
        hasSerial: !!storedSerial,
        model: effectiveModel,
        derivedModel: storedDerivedModel,
        modelSource,
        hasSku: !!storedSku,
      });

      setSessionId(storedSessionId);
      setDeviceSerial(storedSerial);
      setDeviceModelState(effectiveModel);
      setDeviceSku(storedSku);

      // Validate required data
      if (!storedSerial) {
        guideLogger.warn('Missing required session data');
        setSessionStateState('missing_or_expired');
      } else {
        setSessionStateState('ok');
      }
    };

    loadSessionData();
  }, [isV2Route]); // Re-run if route changes

  // Navigation actions
  const goToNextStep = useCallback(() => {
    setStepHistory(prev => [...prev, currentStepIndex]);
    setCurrentStepIndex(prev => Math.min(prev + 1, STEPS.length - 1));
  }, [currentStepIndex]);

  const goToPreviousStep = useCallback(() => {
    setStepHistory(prev => {
      const newHistory = [...prev];
      const previousIndex = newHistory.pop();
      if (previousIndex !== undefined) {
        setCurrentStepIndex(previousIndex);
      }
      return newHistory;
    });
  }, []);

  const goToStep = useCallback((stepId: string) => {
    const index = STEPS.findIndex(s => s.id === stepId);
    if (index >= 0) {
      setStepHistory(prev => [...prev, currentStepIndex]);
      setCurrentStepIndex(index);
      guideLogger.debug(`Navigated to step: ${stepId} (index: ${index})`);
    } else {
      guideLogger.warn(`Step not found: ${stepId}`);
    }
  }, [currentStepIndex]);

  // State update actions
  const setDeviceModel = useCallback((model: CoreModel) => {
    setDeviceModelState(model);
    guideLogger.info(`Device model set to: ${model}`);
  }, []);

  const setConnectionStatus = useCallback((status: ConnectionStatus) => {
    setConnectionStatusState(status);
    guideLogger.debug(`Connection status changed to: ${status}`);
  }, []);

  const setDeviceUrl = useCallback((url: string | null) => {
    setDeviceUrlState(url);
  }, []);

  const incrementRetryCount = useCallback(() => {
    setConnectionRetryCount(prev => prev + 1);
  }, []);

  const resetRetryCount = useCallback(() => {
    setConnectionRetryCount(0);
  }, []);

  const setActiveLayer = useCallback((layer: string | null) => {
    setActiveLayerState(layer);
  }, []);

  const setMainStepId = useCallback((stepId: string) => {
    setMainStepIdState(stepId);
  }, []);

  const setSessionState = useCallback((state: SessionState) => {
    setSessionStateState(state);
  }, []);

  // Computed values
  const currentStep = STEPS[currentStepIndex];
  const currentStepId = currentStep?.id ?? 'loading';
  const currentStepNumber = currentStep?.stepNumber ?? null;
  
  // Show back button for all numbered steps (not loading, welcome, or error screens)
  const showBackButton = currentStepNumber !== null && stepHistory.length > 0;

  const contextValue = useMemo<InstallationGuideContextValue>(() => ({
    // State
    currentStepIndex,
    stepHistory,
    isV2Route,
    sessionId,
    deviceSerial,
    deviceModel,
    deviceSku,
    orgId,
    connectionStatus,
    connectionRetryCount,
    deviceUrl,
    activeLayer,
    mainStepId,
    sessionState,

    // Actions
    goToNextStep,
    goToPreviousStep,
    goToStep,
    setDeviceModel,
    setConnectionStatus,
    setDeviceUrl,
    incrementRetryCount,
    resetRetryCount,
    setActiveLayer,
    setMainStepId,
    setSessionState,

    // Computed
    currentStepId,
    currentStepNumber,
    totalSteps: TOTAL_VISIBLE_STEPS,
    showBackButton,
  }), [
    currentStepIndex,
    stepHistory,
    isV2Route,
    sessionId,
    deviceSerial,
    deviceModel,
    deviceSku,
    orgId,
    connectionStatus,
    connectionRetryCount,
    deviceUrl,
    activeLayer,
    mainStepId,
    sessionState,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    setDeviceModel,
    setConnectionStatus,
    setDeviceUrl,
    incrementRetryCount,
    resetRetryCount,
    setActiveLayer,
    setMainStepId,
    setSessionState,
    currentStepId,
    currentStepNumber,
    showBackButton,
  ]);

  return (
    <InstallationGuideContext.Provider value={contextValue}>
      {children}
    </InstallationGuideContext.Provider>
  );
};
